import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, User, Clock } from 'lucide-react';
import { useUserDashboard } from '../../hooks/userDashboardHooks/useUserDashboard';
import { connectivityAPI } from '../../api/connectivityApis/connectivity.api';

import { AstrologyIntake } from './forms/AstrologyIntake';
import { DoctorIntake } from './forms/DoctorIntake';
import { LawyerIntake } from './forms/LawyerIntake';

interface ConnectivityIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  expertName: string;
  categoryId?: number | null;
  expertCategory?: string;
  expertSubCategories?: string[] | string;
  mode: 'chat' | 'call' | 'video' | null;
  onReady: (
    intakeData: any,
    mode: 'chat' | 'call' | 'video'
  ) => Promise<void> | void;
  queueRank?: number | null;
  onCancelQueue?: () => void;
  onMinimize?: () => void;
}

export const ConnectivityIntakeModal: React.FC<
  ConnectivityIntakeModalProps
> = ({
  isOpen,
  onClose,
  expertName,
  categoryId,
  expertCategory = 'general',
  expertSubCategories,
  mode,
  onReady,
  queueRank,
  onCancelQueue,
  onMinimize
}) => {
  const {
    profile,
    isLoading: userLoading,
    error: userError
  } = useUserDashboard();

  const [formData, setFormData] = useState<Record<string, string>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const category = expertCategory.toLowerCase();

    const isAstrology =
      category === 'astrology' ||
      category === 'astrologer' ||
      categoryId === 1;

    if (!isAstrology) {
      return;
    }

    const userCode =
      profile?.userCode ||
      profile?.user_code ||
      profile?.id ||
      localStorage.getItem('user_code');

    if (!userCode) {
      console.warn(
        '[ConnectivityIntakeModal] User code not available for astrology history.'
      );
      return;
    }

    let isMounted = true;

    const loadPreviousAstrologyIntake = async () => {
      try {
        const history = await connectivityAPI.getSessionHistoryInbox(
          'user',
          userCode
        );

        console.log(
          '[ConnectivityIntakeModal] 🔎 Astrology history response:',
          history
        );

        if (!isMounted || !Array.isArray(history)) {
          console.warn(
            '[ConnectivityIntakeModal] ⚠️ History is not an array:',
            history
          );
          return;
        }

        const previousAstrologySession = history.find((session: any) => {
          const intakeData =
            session?.intake_data ||
            session?.intakeData;

          if (!intakeData) {
            return false;
          }

          const sessionCategoryId =
            intakeData?.categoryId ??
            intakeData?.category_id;

          const sessionCategoryName = String(
            intakeData?.categoryName ??
            intakeData?.category_name ??
            ''
          ).toLowerCase();

          const isAstrologySession =
            Number(sessionCategoryId) === 1 ||
            sessionCategoryName === 'astrology' ||
            sessionCategoryName === 'astrologer';

          if (!isAstrologySession) {
            return false;
          }

          const dynamicFormData =
            intakeData?.dynamicFormData ||
            intakeData?.dynamic_form_data ||
            {};

          const tob = dynamicFormData?.tob;
          const pob = dynamicFormData?.pob;

          return Boolean(
            typeof tob === 'string' &&
            tob.trim() &&
            pob &&
            (
              typeof pob === 'string'
                ? pob.trim() && pob !== '{}'
                : Object.keys(pob).length > 0
            )
          );
        });

        if (!previousAstrologySession) {
          console.log(
            '[ConnectivityIntakeModal] ❌ No previous astrology intake found.'
          );

          console.log(
            '[ConnectivityIntakeModal] History length:',
            history.length
          );

          return;
        }

        console.log(
          '[ConnectivityIntakeModal] ✅ Previous astrology session found:',
          previousAstrologySession
        );

        const intakeData =
          previousAstrologySession?.intake_data ||
          previousAstrologySession?.intakeData;

        const dynamicFormData =
          intakeData?.dynamicFormData ||
          intakeData?.dynamic_form_data ||
          {};

        console.log(
          '[ConnectivityIntakeModal] 🔎 Previous astrology dynamicFormData:',
          dynamicFormData
        );

        const previousTob =
          typeof dynamicFormData.tob === 'string'
            ? dynamicFormData.tob
            : '';

        const previousPob =
          typeof dynamicFormData.pob === 'string'
            ? dynamicFormData.pob
            : dynamicFormData.pob
              ? JSON.stringify(dynamicFormData.pob)
              : '';

        console.log(
          '[ConnectivityIntakeModal] 🔎 Previous TOB:',
          previousTob
        );

        console.log(
          '[ConnectivityIntakeModal] 🔎 Previous POB:',
          previousPob
        );

        if (
          !previousTob ||
          !previousPob ||
          previousPob === '{}'
        ) {
          console.warn(
            '[ConnectivityIntakeModal] ⚠️ Previous astrology session exists, but TOB/POB are empty.'
          );
          return;
        }

        setFormData(prev => {
          const updatedFormData = {
            ...prev,

            // Do not overwrite anything the user has already entered.
            tob: prev.tob?.trim()
              ? prev.tob
              : previousTob,

            pob: prev.pob?.trim()
              ? prev.pob
              : previousPob,
          };

          console.log(
            '[ConnectivityIntakeModal] 🔄 Updating formData with previous astrology details:',
            updatedFormData
          );

          return updatedFormData;
        });

        console.log(
          '[ConnectivityIntakeModal] ✅ Previous astrology birth details prefilled.'
        );

      } catch (error) {
        console.warn(
          '[ConnectivityIntakeModal] ⚠️ Failed to load previous astrology intake:',
          error
        );
      }
    };

    loadPreviousAstrologyIntake();

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    expertCategory,
    expertSubCategories,
    categoryId,
    profile?.userCode,
    profile?.user_code,
    profile?.id,
  ]);

  if (!isOpen || !mode) return null;

  const handleInputChange = (
    id: string,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalCategoryId = categoryId;

      if (!finalCategoryId) {
        const catLower = expertCategory.toLowerCase();

        if (
          catLower === 'astrology' ||
          catLower === 'astrologer'
        ) {
          finalCategoryId = 1;
        }

        if (
          catLower === 'lawyer' ||
          catLower === 'legal'
        ) {
          finalCategoryId = 2;
        }

        if (
          catLower === 'doctor' ||
          catLower === 'medical'
        ) {
          finalCategoryId = 3;
        }
      }

      const guaranteedUserCode =
        profile?.user_code ||
        profile?.userCode ||
        profile?.id ||
        localStorage.getItem('user_code');

      await onReady(
        {
          categoryId: finalCategoryId || null,
          categoryName: expertCategory,
          subCategories: expertSubCategories,
          dynamicFormData: formData,

          userProfile: {
            ...profile,
            user_code: guaranteedUserCode
          },

          userCode: guaranteedUserCode
        },
        mode
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatSubCategories = () => {
    if (!expertSubCategories) return '';

    return Array.isArray(expertSubCategories)
      ? expertSubCategories.join(', ')
      : expertSubCategories;
  };

  const renderDynamicForm = () => {
    const category = expertCategory.toLowerCase();

    switch (category) {
      case 'astrology':
      case 'astrologer':
        return (
          <AstrologyIntake
            formData={formData}
            onChange={handleInputChange}
          />
        );

      case 'doctor':
      case 'medical':
        return (
          <DoctorIntake
            formData={formData}
            onChange={handleInputChange}
          />
        );

      case 'lawyer':
      case 'legal':
        return (
          <LawyerIntake
            formData={formData}
            onChange={handleInputChange}
          />
        );

      default:
        return (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              What is your main query?
            </label>

            <textarea
              rows={3}
              placeholder="How can the expert help you today?"
              value={formData.query || ''}
              onChange={(e) =>
                handleInputChange(
                  'query',
                  e.target.value
                )
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
            />
          </div>
        );
    }
  };

  const category = expertCategory.toLowerCase();

  const isAstrology =
    category === 'astrology' ||
    category === 'astrologer';

  const isAstroFormIncomplete =
    isAstrology &&
    (
      !formData.tob?.trim() ||
      !formData.pob?.trim() ||
      formData.pob === '{}'
    );

  const isLawyer =
    category === 'lawyer' ||
    category === 'legal';

  const isLawyerFormIncomplete =
    isLawyer &&
    (
      !formData.location?.trim() ||
      !formData.description?.trim()
    );

  const isDoctor =
    category === 'doctor' ||
    category === 'medical';

  const isDoctorFormIncomplete =
    isDoctor &&
    (
      !formData.symptoms?.trim() ||
      !formData.duration?.trim()
    );

  const isSubmitDisabled =
    userLoading ||
    isAstroFormIncomplete ||
    isLawyerFormIncomplete ||
    isDoctorFormIncomplete;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200 overflow-y-auto">

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col my-auto relative">

        {queueRank && onCancelQueue ? (

          <div className="p-8 flex flex-col items-center text-center">

            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-amber-500" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              You're in Line!
            </h3>

            <p className="text-sm text-gray-500 mb-8 px-4">
              <span className="font-bold text-gray-900">
                {expertName}
              </span>{' '}
              is currently assisting another client. Please do not close this window.
            </p>

            <div className="w-full bg-white border border-gray-100 shadow-sm rounded-xl p-5 mb-6">
              <p className="text-[11px] font-bold text-indigo-400 uppercase tracking-wider mb-1">
                Your Position
              </p>

              <p className="text-5xl font-black text-indigo-600">
                #{queueRank}
              </p>
            </div>

            <div className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 font-medium mb-8 bg-gray-50 py-3 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
              Waiting for expert to be ready...
            </div>

            <div className="flex items-center justify-center gap-3 w-full">

              <button
                onClick={onCancelQueue}
                className="flex-1 py-2.5 px-4 border-2 border-red-500 text-red-500 font-bold rounded-xl hover:bg-red-500 hover:text-white transition-all duration-200 text-sm"
              >
                Cancel Request
              </button>

              <button
                onClick={onMinimize}
                className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 font-bold rounded-xl transition-all duration-200 text-sm"
              >
                Minimize
              </button>

            </div>

          </div>

        ) : (

          <>

            <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gray-50 shrink-0">

              <div className="pr-4">

                <h3 className="font-bold text-lg text-gray-900 capitalize">
                  Start {mode}
                </h3>

                <p className="text-[13px] text-gray-500 mt-1 leading-snug">

                  Connecting with{' '}

                  <span className="capitalize">
                    {expertCategory}
                  </span>{' '}

                  <span className="text-amber-500 font-bold">
                    {expertName}
                  </span>

                  {expertSubCategories && (
                    <span>
                      , expertise in{' '}
                      <span className="font-medium text-gray-600">
                        {formatSubCategories()}
                      </span>
                    </span>
                  )}

                </p>

              </div>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-gray-200 hover:bg-gray-300 p-1.5 rounded-full transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>

            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">

              <h4 className="text-sm font-bold text-gray-900 mb-5 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-gray-100">
                <AlertCircle className="w-4 h-4 text-emerald-500" />
                Pre-Consultation Details
              </h4>

              <div className="mb-6 space-y-4">

                {userLoading ? (

                  <div className="flex justify-center py-2">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  </div>

                ) : userError ? (

                  <p className="text-xs text-red-500">
                    Failed to load profile details.
                  </p>

                ) : (

                  <div className="grid grid-cols-2 gap-4">

                    <div className="flex flex-col gap-1 col-span-2 sm:col-span-1">

                      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                        Client
                      </label>

                      <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">

                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden shrink-0">

                          {profile?.profilePhotoUrl ? (

                            <img
                              src={profile.profilePhotoUrl}
                              alt="User"
                              className="w-full h-full object-cover"
                            />

                          ) : (

                            <User className="w-3.5 h-3.5 text-blue-500" />

                          )}

                        </div>

                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {profile?.name || 'N/A'}
                        </p>

                      </div>

                    </div>

                    <div className="flex flex-col gap-1 col-span-2 sm:col-span-1">

                      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                        Gender
                      </label>

                      <p className="text-sm font-semibold text-gray-900 capitalize bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 h-full flex items-center">
                        {profile?.gender || 'N/A'}
                      </p>

                    </div>

                    <div className="flex flex-col gap-1 col-span-2">

                      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                        Date of Birth
                      </label>

                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                        {profile?.dob
                          ? new Date(profile.dob).toLocaleDateString('en-GB')
                          : 'N/A'}
                      </p>

                    </div>

                  </div>

                )}

              </div>

              {renderDynamicForm()}

            </div>

            <div className="p-5 border-t border-gray-100 bg-white shrink-0">

              <button
                disabled={
                  isSubmitDisabled ||
                  isSubmitting
                }
                onClick={handleSubmit}
                className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  !(isSubmitDisabled || isSubmitting)
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >

                {isSubmitting ? (

                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting...
                  </>

                ) : (

                  `Start ${
                    mode === 'video'
                      ? 'Video Call'
                      : mode === 'call'
                        ? 'Voice Call'
                        : 'Chat'
                  } Now`

                )}

              </button>

            </div>

          </>

        )}

      </div>

    </div>
  );
};